import { useState, useMemo } from 'react';
import { Filter } from 'lucide-react';
import {
  DataTable, EmptyState, FilterBuilder, Pagination, StatusPill,
  type ColumnDef, type FilterCondition, type FilterField,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Badge, Button, Popover, PopoverContent, PopoverTrigger } from '@vnetwork/ui/primitives';
import { Toolbar } from '@vnetwork/ui/layout';
import { formatDateTime, formatMoney } from '@/lib/format';
import { useAffiliateCommissions, type AffiliateCommission, type CommissionStatus } from '@/api/affiliate';
import { paginationView } from '@/pages/customers/pagination-summary';
import { EntityLink } from '@/components/entity-link';
import { TableLink } from '@/components/table-link';
import { commissionTone, humanize } from './status-tones';

const PAGE_SIZE = 20;

interface CommissionsTabProps {
  affiliateId: number;
}

const COMMISSION_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'refunded', label: 'Refunded' },
];

const FILTER_FIELDS: FilterField[] = [
  { value: 'status', label: 'Status', input: 'select', options: COMMISSION_STATUS_OPTIONS },
];

/**
 * Shared column definitions for the commissions table.
 * `linkInvoices` — when true (manager view), invoice IDs are linked to `/invoices/:id`.
 */
export function buildCommissionColumns(
  linkInvoices = true,
): ColumnDef<AffiliateCommission, unknown>[] {
  return [
    {
      id: 'commission_id',
      header: 'ID',
      cell: ({ row }) => row.original.commission_id,
    },
    {
      id: 'invoice_id',
      header: 'Invoice',
      cell: ({ row }) =>
        row.original.invoice_id ? (
          linkInvoices ? (
            <TableLink to={`/invoices/${row.original.invoice_id}`}>
              {row.original.invoice_id}
            </TableLink>
          ) : (
            String(row.original.invoice_id)
          )
        ) : (
          '—'
        ),
    },
    {
      id: 'revenue_amount',
      header: () => <div style={{ textAlign: 'right' }}>Revenue</div>,
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>{formatMoney(row.original.revenue_amount)}</div>
      ),
    },
    {
      id: 'commission_percent',
      header: 'Rate',
      cell: ({ row }) => `${row.original.commission_percent}%`,
    },
    {
      id: 'commission_amount',
      header: () => <div style={{ textAlign: 'right' }}>Commission</div>,
      cell: ({ row }) => (
        <div style={{ textAlign: 'right', fontWeight: 'var(--vn-font-weight-semibold)' }}>
          {formatMoney(row.original.commission_amount)}
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusPill tone={commissionTone(row.original.status)}>
          {humanize(row.original.status)}
        </StatusPill>
      ),
    },
    {
      id: 'customer_id',
      header: 'Customer',
      cell: ({ row }) =>
        row.original.customer_id ? (
          <EntityLink
            to={`/customers/${row.original.customer_id}`}
            id={row.original.customer_id}
          />
        ) : (
          '—'
        ),
    },
    {
      id: 'created_at',
      header: 'Created',
      cell: ({ row }) => formatDateTime(row.original.created_at),
    },
    {
      id: 'confirmed_at',
      header: 'Confirmed',
      cell: ({ row }) => formatDateTime(row.original.confirmed_at),
    },
  ];
}

/**
 * Commissions tab — server-paginated commission audit trail with status filter bar.
 */
export function CommissionsTab({ affiliateId }: CommissionsTabProps) {
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);

  const statusFilter = conditions.find((c) => c.field === 'status')?.value as CommissionStatus | undefined;

  const { data, isLoading, isError, refetch } = useAffiliateCommissions(affiliateId, {
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    ...(statusFilter ? { status: statusFilter } : {}),
  });

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => buildCommissionColumns(true), []);

  const appliedCount = new Set(conditions.map((c) => c.field)).size;

  const applyFilters = (next: FilterCondition[]) => {
    setConditions(next.filter((c) => c.value !== ''));
    setPage(1);
    setFilterOpen(false);
  };

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load commissions"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Toolbar
        style={{ marginBottom: 0 }}
        end={
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="secondary" startIcon={<Filter size={15} />}>
                Filters{appliedCount ? <Badge tone="primary">{appliedCount}</Badge> : null}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" style={{ padding: 0 }}>
              <FilterBuilder
                fields={FILTER_FIELDS}
                defaultValue={conditions.length ? conditions : undefined}
                onApply={applyFilters}
                onClose={() => setFilterOpen(false)}
              />
            </PopoverContent>
          </Popover>
        }
      />

      <DataTable
        columns={columns}
        data={rows}
        enableSorting={false}
        getRowId={(r) => String(r.commission_id)}
        emptyState={
          <EmptyState
            title="No commissions"
            description="Commission records for this affiliate will appear here."
          />
        }
        footer={
          isLoading ? null : (
            <Pagination
              page={page}
              pageCount={view.pageCount}
              onPageChange={setPage}
              summary={view.summary}
            />
          )
        }
      />
    </div>
  );
}
